import { ValueGetterParams } from 'ag-grid-community'
import {
  AggregateField,
  WbsItemType,
} from '../../../../../../domain/entity/WbsItemEntity'
import {
  ProgramManagementGridContext,
  ProgramManagementProgress,
  ProgramManagementRow,
} from '../../../programManagement'

type ProductivityMetric = 'sv' | 'spi' | 'cv' | 'cpi' | 'etc' | 'eac' | 'vac'

/** context の aggregateTarget / aggregateField に応じた進捗データを返す */
const getActiveProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  const isCount = !ctx || ctx.aggregateField === AggregateField.WBS_ITEM_COUNT

  if (isTask) {
    return isCount ? progress.taskCount : progress.taskWorkload
  }
  return isCount ? progress.deliverableCount : progress.deliverableWorkload
}

/**
 * ネストされたデータから値を取得する。
 * progress カテゴリの場合は context に応じたバリエーションを選択する。
 */
export const getAggregatedValue = (
  params: ValueGetterParams<ProgramManagementRow>,
  category: 'progress' | 'resource' | 'profitLoss',
  field: string
): number | null => {
  const data = params.data
  if (!data) return null

  if (category === 'progress') {
    const progress = getActiveProgress(params)
    if (!progress) return null
    const value = (progress as Record<string, unknown>)[field]
    return typeof value === 'number' ? value : null
  }

  const categoryData = data[category]
  if (!categoryData) return null
  const value = (categoryData as Record<string, unknown>)[field]
  return typeof value === 'number' ? value : null
}

/**
 * 生産性指標の計算には常に工数(workload)ベースの進捗を使用する。
 * actualResource（sumActualHour）が時間単位のため、
 * EV/PV/BAC も同じ時間単位の工数進捗値を直接使用する。
 */
const getWorkloadProgress = (
  params: ValueGetterParams<ProgramManagementRow>
): ProgramManagementProgress | null => {
  const progress = params.data?.progress
  if (!progress) return null

  const ctx = params.context as ProgramManagementGridContext | undefined
  const isTask = ctx?.aggregateTarget === WbsItemType.TASK
  return isTask ? progress.taskWorkload : progress.deliverableWorkload
}

/**
 * 進捗とリソースから EVM 生産性指標を算出する。
 *
 * EV = completed（完了工数: 時間）
 * PV = scheduledCompleted（予定完了工数: 時間）
 * AC = actualResource（投入工数: 時間）
 * BAC = total（総工数: 時間）
 */
export const calculateProductivity = (
  params: ValueGetterParams<ProgramManagementRow>,
  metric: ProductivityMetric
): number | null => {
  const data = params.data
  if (!data) return null

  const progress = getWorkloadProgress(params)
  if (!progress) return null

  const { total, scheduledCompleted, completed } = progress

  const ev = completed
  const pv = scheduledCompleted
  const bac = total
  // AC（投入工数）: PROJECT/PROGRAM/GROUP 行は resource（KPI レポート/集計）から、
  // resource を持たない WORKGROUP/PROCESS 行は cumulation の実績時間から取得する
  // （プロジェクト計画の EVM 列と同じ出所）
  const ac =
    data.resource?.actualResource ??
    data.projectPlanCumulation?.sumActualHour ??
    null
  const cpi = ac == null || ac === 0 ? null : ev / ac

  switch (metric) {
    case 'sv':
      return ev - pv
    case 'spi':
      return pv === 0 ? null : ev / pv
    case 'cv':
      return ac == null ? null : ev - ac
    case 'cpi':
      return cpi
    case 'etc':
      return !cpi ? null : (bac - ev) / cpi
    case 'eac':
      return !cpi ? null : bac / cpi
    case 'vac': {
      // VAC = BAC - EAC（プロジェクト計画の Progress.varianceAtCompletion と同式。
      // EAC が算出不能または 0 の場合は非表示）
      const eac = !cpi ? null : bac / cpi
      return !eac ? null : bac - eac
    }
    default:
      return null
  }
}
